import { Link as RouterLink } from 'react-router-dom';
import {
  Card,
  CardContent,
  Chip,
  Collapse,
  IconButton,
  Link,
  makeStyles,
  Theme,
  Tooltip,
  Typography,
} from '@material-ui/core';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { useTranslation } from 'react-i18next';
import { canPerform, RoleGate } from '../../roles';
import { Project } from './types';
import { useProjectMemberCount } from './useProjectMemberCount';

const useStyles = makeStyles((theme: Theme) => ({
  actionButton: {
    border: `1px solid ${theme.palette.divider}`,
  },
  actionRow: {
    marginTop: theme.spacing(1),
    display: 'flex',
    justifyContent: 'flex-end',
  },
  metricValue: {
    fontWeight: theme.typography.fontWeightBold as number,
  },
  projectCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  projectCardFooter: {
    marginTop: theme.spacing(2),
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: theme.spacing(2),
  },
  projectCardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
  },
  projectCardMeta: {
    marginTop: theme.spacing(2),
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  subtle: {
    color: theme.palette.text.secondary,
  },
}));

type ProjectCardProps = {
  expanded: boolean;
  isProjectPreviewMode: boolean;
  project: Project;
  onDelete: () => void;
  onToggleActions: () => void;
};

export function ProjectCard(props: ProjectCardProps) {
  const classes = useStyles();
  const { t } = useTranslation('projects');
  const { project } = props;
  const memberCount = useProjectMemberCount(
    project,
    props.isProjectPreviewMode,
  );

  return (
    <Card variant="outlined" className={classes.projectCard}>
      <CardContent>
        <div className={classes.projectCardHeader}>
          <div>
            <Link
              component={RouterLink}
              to={`/projects/${project.id}`}
              color="primary"
              variant="h6"
            >
              {project.name}
            </Link>
            <Typography variant="body2" className={classes.subtle}>
              {project.description}
            </Typography>
          </div>
          <Tooltip title={t('actions.projectActions')}>
            <IconButton
              size="small"
              aria-label={t('actions.projectActions')}
              onClick={props.onToggleActions}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>

        <Collapse in={props.expanded}>
          <div className={classes.actionRow}>
            <Tooltip title={t('actions.editProject')}>
              <span>
                <IconButton
                  className={classes.actionButton}
                  size="small"
                  aria-label={t('actions.editProject')}
                  component={RouterLink}
                  to={`/projects/${project.id}`}
                  disabled={!canPerform(project.role, 'project.settings')}
                  onClick={props.onToggleActions}
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <RoleGate projectRole={project.role} action="project.delete">
              <Tooltip title={t('actions.deleteProject')}>
                <span>
                  <IconButton
                    className={classes.actionButton}
                    size="small"
                    aria-label={t('actions.deleteProject')}
                    onClick={props.onDelete}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </RoleGate>
          </div>
        </Collapse>

        <div className={classes.projectCardMeta}>
          <Chip label={t(`roles.${project.role}`)} size="small" />
          <Chip label={project.organization} size="small" variant="outlined" />
          {project.owner && (
            <Chip
              label={t('labels.owner', { name: project.owner })}
              size="small"
              variant="outlined"
            />
          )}
          <Chip
            label={t('labels.updatedWithDate', {
              date: project.updatedAt,
            })}
            size="small"
            variant="outlined"
          />
        </div>

        <div className={classes.projectCardFooter}>
          <ProjectMetric
            value={project.workItems.length}
            label={t('labels.workItems')}
          />
          <ProjectMetric
            value={project.artifacts.length}
            label={t('labels.artifacts')}
          />
          <ProjectMetric
            value={memberCount ?? '–'}
            label={t('labels.members')}
          />
        </div>
      </CardContent>
    </Card>
  );
}

function ProjectMetric(props: { value: number | string; label: string }) {
  const classes = useStyles();

  return (
    <div>
      <Typography variant="h6" className={classes.metricValue}>
        {props.value}
      </Typography>
      <Typography variant="body2" className={classes.subtle}>
        {props.label}
      </Typography>
    </div>
  );
}
